import {
  AutoComplete,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popover,
  Select,
  Spin,
  Upload,
} from 'antd'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {theme} from 'antd'
import {IntlShape, useIntl} from 'react-intl'
import debounce from 'lodash/debounce'
import {instance as projectsService} from '../../services/ProjectsService'
import {ProjectAddress, ProjectAddressMapPosition} from '../../entities/project'
import {useRevalidator} from 'react-router-dom'
import {SearchOutlined} from '@ant-design/icons'
import {EditorProjectMap, MapImageExtractor, MapMetersPerPixelExtractor} from './EditorProjectMap'

export type LocationInputProps = {
  // currentLocation?: ProjectAddress
  debounceTimeout?: number
  chosenLocation?: ProjectAddress
  onChange?: (newValue: ProjectAddress) => void
  onMapImageExtractorReady?: (extractor: MapImageExtractor) => void
}

export function getMapLocationFromAddress(address: ProjectAddress) {
  return {
    latitude: address.latitude,
    longtitude: address.longtitude,
    zoomLevel: 18,
  }
}

export async function getNewLocationWithMap(
  intl: IntlShape,
  currentLocation: ProjectAddress,
  newLocation: ProjectAddress
): Promise<ProjectAddress> {
  let result = {...newLocation}

  if (!currentLocation?.map) {
    return result
  }

  if (currentLocation.map) {
    const mapBehavior = await new Promise<'preserve' | 'overwrite' | 'cancel'>((resolve) => {
      const {destroy} = Modal.confirm({
        title: intl.formatMessage({id: 'confirm'}),
        content: intl.formatMessage({id: 'mapCoordinatesWillBeReset'}),
        onOk() {
          resolve('overwrite')
        },
        onCancel() {
          resolve('cancel')
        },
        footer: (_, {OkBtn, CancelBtn}) => (
          <>
            <CancelBtn />
            <Button
              type="primary"
              onClick={() => {
                destroy()
                resolve('preserve')
              }}
            >
              {intl.formatMessage({id: 'preserveMapCoordinates'})}
            </Button>
            <OkBtn />
          </>
        ),
      })
    })

    if (mapBehavior === 'cancel') {
      // no update to location or map - new location is actually the old one
      newLocation = currentLocation
    } else if (mapBehavior === 'preserve') {
      // location is updated, but map is used from the old one
      newLocation.map = currentLocation.map
    } else {
      // both location and map are updated
      newLocation.map = getMapLocationFromAddress(newLocation)
    }

    return newLocation
  }
}

const requiredSearchQueryLength = 5

export function LocationInput({
  // currentLocation,
  debounceTimeout = 500,
  onChange: onChosenLocationChange,
  chosenLocation,
  onMapImageExtractorReady,
}: LocationInputProps) {
  const {
    token: {colorPrimary, colorTextSecondary},
  } = theme.useToken()

  const revalidator = useRevalidator()

  const fetchRef = useRef(0)

  const [isSelectorVisible, setSelectorVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<ProjectAddress[]>([])
  const [searchText, setSearchText] = useState('')
  const intl = useIntl()
  const [suggestionsListOpen, setSuggestionsListOpen] = useState(false)
  const mapMetersPerPixelExtractorRef = useRef<MapMetersPerPixelExtractor | null>(null)

  const loadOptions = async () => {
    fetchRef.current += 1
    const fetchId = fetchRef.current

    if (!searchText || searchText.length < requiredSearchQueryLength) {
      setLoading(false)
      setOptions([])
      return
    }

    setOptions([])

    setLoading(true)

    await new Promise((resolve) => setTimeout(resolve, 1000))

    const searchResult: ProjectAddress[] =
      /*[
      {
        addressName: 'test 1',
        latitude: 0,
        longtitude: 0,
      },
      {
        addressName: searchText,
        latitude: 0,
        longtitude: 0,
      },
    ]*/
      await projectsService.searchLocation(searchText)

    if (fetchId != fetchRef.current) {
      return
    }

    setLoading(false)
    setOptions(searchResult) //setOptions(searchResult.data?.data?.records || [])
    setSuggestionsListOpen(true)
  }

  const renderLocationSelectPopover = (content) => (
    <Popover
      destroyTooltipOnHide
      content={
        <AutoComplete
          /*disabled={loading}*/
          style={{width: 400}}
          // ref={(e) => requestAnimationFrame(() => e?.focus())}
          filterOption={false}
          open={suggestionsListOpen}
          autoFocus
          onFocus={() => setSuggestionsListOpen(true)}
          onChange={setSearchText}
          autoClearSearchValue={false}
          notFoundContent={loading ? <Spin size="small" /> : null}
          onSelect={async (_, option) => {
            let newLocation: ProjectAddress = {
              addressName: option.label,
              latitude: option.latitude,
              longtitude: option.longtitude,
            }

            const newLocationWithMap = await getNewLocationWithMap(
              intl,
              chosenLocation,
              newLocation
            )

            if (typeof mapMetersPerPixelExtractorRef.current === 'function') {
              newLocationWithMap.map.mapMetersPerPixel = mapMetersPerPixelExtractorRef.current()
            }

            onChosenLocationChange(newLocationWithMap)
            setSuggestionsListOpen(false)
            setSelectorVisible(false)
          }}
          value={searchText}
          placeholder={intl.formatMessage({id: 'searchForLocation'})}
          options={(options || []).map((option, index) => ({
            value: String(index),
            label: option.addressName,
            latitude: option.latitude,
            longtitude: option.longtitude,
          }))}
        >
          <Input.Search
            enterButton={
              <Button
                disabled={searchText.length < requiredSearchQueryLength}
                loading={loading}
                type="primary"
                icon={<SearchOutlined />}
                onClick={loadOptions}
              ></Button>
            }
            loading={loading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                loadOptions()
              }
            }}
          ></Input.Search>
        </AutoComplete>
      }
      trigger="click"
      open={isSelectorVisible}
      onOpenChange={setSelectorVisible}
      placement="topLeft"
    >
      {content}
    </Popover>
  )

  return (
    <div
      style={{
        color: colorPrimary,
      }}
    >
      {chosenLocation && (
        <div>
          <div>{chosenLocation.addressName}</div>
          <div style={{color: colorTextSecondary}}>
            ({chosenLocation.latitude}; {chosenLocation.longtitude})
          </div>
        </div>
      )}
      {renderLocationSelectPopover(
        <Button>
          {chosenLocation // currentLocation
            ? intl.formatMessage({id: 'changeProjectLocation'})
            : intl.formatMessage({id: 'enterProjectLocation'})}
        </Button>
      )}
      <Button
        style={{margin: '0 10px'}}
        onClick={async () => {
          const userLocation = await projectsService.getUserLocation()

          const newLocationWithMap = await getNewLocationWithMap(
            intl,
            chosenLocation, // currentLocation,
            userLocation
          )

          if (typeof mapMetersPerPixelExtractorRef.current === 'function') {
            newLocationWithMap.map.mapMetersPerPixel = mapMetersPerPixelExtractorRef.current()
          }

          onChosenLocationChange(newLocationWithMap)
          revalidator.revalidate()
        }}
      >
        {intl.formatMessage({id: 'useMyCurrentLocation'})}
      </Button>

      <Checkbox
        checked={Boolean(chosenLocation?.map)}
        disabled={!chosenLocation}
        onChange={(e) => {
          const map = e.target.checked ? getMapLocationFromAddress(chosenLocation) : null

          onChosenLocationChange({
            ...chosenLocation,
            map,
          })
        }}
      >
        {intl.formatMessage({id: 'useMapForProjectCreating'})}
      </Checkbox>

      {chosenLocation && chosenLocation.map && (
        <div>
          <div>{intl.formatMessage({id: 'placeTheMap'})}</div>
          <EditorProjectMap
            mapPosition={chosenLocation.map}
            onMapPositionUpdated={(position) =>
              onChosenLocationChange({
                ...chosenLocation,
                map: position,
              })
            }
            onMapImageExtractorReady={onMapImageExtractorReady}
            onMetersPerPixelExtractorReady={(extractor) => {
              mapMetersPerPixelExtractorRef.current = extractor

              onChosenLocationChange({
                ...chosenLocation,
                map: {
                  ...chosenLocation.map,
                  mapMetersPerPixel: extractor(),
                },
              })
            }}
          />
        </div>
      )}
    </div>
  )
}
